import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, withDirectives as _withDirectives, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "crz-container mt-5" }
const _hoisted_2 = {
  key: 0,
  class: "f-24 mb-2 font-weight-medium"
}
const _hoisted_3 = {
  key: 1,
  class: "f-24 mb-2 font-weight-medium"
}
const _hoisted_4 = {
  key: 2,
  class: "f-18 mb-0"
}
const _hoisted_5 = { class: "mb-0" }
const _hoisted_6 = ["href"]

import CaptchaField from '@/components/FamilyChoiceForm/CaptchaField.vue';
import CRZ from '@/components/widgets/CRZ.vue';
import RadioGroup from '@/components/widgets/Inputs/RadioGroup.vue';
import TextArea from '@/components/widgets/Inputs/TextArea.vue';
import TextField from '@/components/widgets/Inputs/TextField.vue';
import ModalCustom from '@/components/widgets/ModalCustom.vue';
import { LINKS } from '@/constants/externalLinks';
import { createFamilyChosenEvent, createFormErrorsEvent, createFormSubmitEvent } from '@/plugins/gtm/events';
import { useConsentStore } from '@/stores/Consent';
import { useDonorStore } from '@/stores/Donor';
import type { PersonalDataForm, PersonalDataPreparedForBackend } from '@/types';
import { useGtm } from '@gtm-support/vue-gtm';
import axios from 'axios';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
// @ts-ignore
import { mask } from 'vue-the-mask';
import { useGoTo } from 'vuetify';
import { useStore } from 'vuex';
import ExpandableText from '@/components/widgets/ExpandableText.vue';
import { handleSentryError } from '@/utils/handleSentryError';
import type { GenericObject, InvalidSubmissionContext } from 'vee-validate';
import ROUTE_NAMES from '@/constants/routeNames';
type Props = {
  warehouseId: number;
  captchaRequired: boolean;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'PersonalDataForm',
  props: {
    warehouseId: {},
    captchaRequired: { type: Boolean }
  },
  setup(__props: any) {

/* eslint-disable camelcase */
const props = __props;

const goTo = useGoTo();
const gtm = useGtm();
// vuex
const store = useStore();
const router = useRouter();
const route = useRoute();
const vMask = mask;

const donorStore = useDonorStore();

const phoneMask = {
    mask: 'FFF FFF FFF FFF FFF FFF FF',
    tokens: {
        F: {
            pattern: /\+|[0-9]/
        }
    }
};

const szpPaymentLink: string = LINKS.szpPaymentForm;

const form = ref<PersonalDataForm>({
    email: '',
    phone_number: '',
    first_name: '',
    last_name: '',
    comment: '',
    category: null,
    consents: {},
    captchaToken: null
});

const { handleSubmit } = useForm();

const prepareFormDataForSubmit = (form: PersonalDataForm): PersonalDataPreparedForBackend => {
    const { captchaToken, phone_number, ...rest } = form;

    const formData: PersonalDataPreparedForBackend = {
        ...rest,
        ...(captchaToken && { 'g-recaptcha-response': captchaToken }),
        phone_number: phone_number?.replace(/\s+/g, '')
    };

    return formData;
};

const onValidSubmit = async (): Promise<void> => {
    const preparedFormData: PersonalDataPreparedForBackend = prepareFormDataForSubmit(form.value);
    const familyEditionId: string | string[] = route.params.familyEditionId;
    const warehouseId: number = props.warehouseId;

    try {
        const donor = await store.dispatch('donor/postDonor', {
            familyEditionId,
            warehouseId,
            data: preparedFormData
        });

        donorStore.setDonor(donor);

        const gtmEventValue = {
            familyEditionId,
            warehouseId
        };
        if (!gtm) {
            handleSentryError(new Error('GTM not initialized'));
        } else {
            gtm.trackEvent(createFormSubmitEvent(gtmEventValue));
            gtm.trackEvent(createFamilyChosenEvent(gtmEventValue));
        }
        router.push({ name: ROUTE_NAMES.THANKS });
    } catch (error) {
        if (error instanceof Error) handleSentryError(error);
        const isAxiosError = axios.isAxiosError(error);
        if (isAxiosError && error.response?.status === 404) {
            donorResponse.value.isWarehouseValid = error.response.data.data.isWarehouseValid;
            donorResponse.value.isFamilyChosen = error.response.data.data.isFamilyChoosen;
        }
    }
};

const onInvalidSubmit = ({ errors }: InvalidSubmissionContext<GenericObject, GenericObject>): void => {
    if (!gtm) {
        handleSentryError(new Error('GTM not initialized'));
    } else {
        gtm.trackEvent(createFormErrorsEvent(errors));
    }
    const errorTextElement = document.querySelector<HTMLDivElement>('div.v-input--error');
    if (!errorTextElement) return;
    goTo(errorTextElement, {
        easing: 'easeInQuad'
    });
};
const onSubmit = handleSubmit(onValidSubmit, onInvalidSubmit);

const isFamilyNotAvailable = ref<boolean>(false);

const donorResponse = ref({
    isWarehouseValid: true,
    isFamilyChosen: false
});

watch(donorResponse, (value) => {
    isFamilyNotAvailable.value = value.isWarehouseValid === false || value.isFamilyChosen === true;
}, { deep: true });

const consentStore = useConsentStore();

const getLoader = computed(() => store.getters['donor/getLoader']);
const bubbleId = computed(() => store.getters['families/getBubbleId']);
const consentConfig = computed(() => consentStore.consent ?? { fieldsets: [] });
const getGiftgiverCategories = computed(() => consentStore.giftgiverCategories);
const groupLabels = computed(() => getGiftgiverCategories.value?.map((giftGiverCategory) => ({
    label: giftGiverCategory?.description,
    value: giftGiverCategory?.id
})));
const returnLink = computed(() => ({
    toName: bubbleId.value ? ROUTE_NAMES.FAMILIES_LIST : ROUTE_NAMES.HOME,
    params: bubbleId.value ? { bubbleId: bubbleId.value } : {}
}));
const crzTextFields = computed(() => consentStore.getSplittedLabelTextFields);

return (_ctx: any,_cache: any) => {
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(_component_v_row, { "no-gutters": "" }, {
    default: _withCtx(() => [
      _createVNode(_component_v_col, { cols: "12" }, {
        default: _withCtx(() => [
          _createElementVNode("form", null, [
            _createVNode(_component_v_row, { "no-gutters": "" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_col, {
                  cols: "12",
                  sm: "6",
                  md: "4"
                }, {
                  default: _withCtx(() => [
                    _createVNode(TextField, {
                      modelValue: form.value.email,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.value.email) = $event)),
                      modelModifiers: { trim: true },
                      rules: "required|email_without_polish|max:255",
                      name: "email",
                      autocomplete: "email",
                      inputmode: "email",
                      label: _ctx.$t('label.email'),
                      required: ""
                    }, null, 8, ["modelValue", "label"]),
                    _withDirectives(_createVNode(TextField, {
                      modelValue: form.value.phone_number,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.phone_number) = $event)),
                      rules: "required|phoneNumber",
                      label: _ctx.$t('label.phoneNumber'),
                      name: "phone",
                      autocomplete: "tel",
                      inputmode: "tel",
                      required: "",
                      type: "tel"
                    }, null, 8, ["modelValue", "label"]), [
                      [_unref(vMask), phoneMask]
                    ]),
                    _createVNode(TextField, {
                      modelValue: form.value.first_name,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.first_name) = $event)),
                      rules: "required|name|max:255",
                      name: _ctx.$t('label.firstName'),
                      autocomplete: "given-name",
                      label: _ctx.$t('label.firstName'),
                      required: ""
                    }, null, 8, ["modelValue", "name", "label"]),
                    _createVNode(TextField, {
                      modelValue: form.value.last_name,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.last_name) = $event)),
                      rules: "required|name|max:255",
                      name: _ctx.$t('label.lastName'),
                      autocomplete: "family-name",
                      label: _ctx.$t('label.lastName'),
                      required: ""
                    }, null, 8, ["modelValue", "name", "label"]),
                    _createVNode(TextArea, {
                      modelValue: form.value.comment,
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.value.comment) = $event)),
                      name: "comment",
                      rules: "regularField",
                      label: _ctx.$t('label.formAndTime'),
                      placeholder: _ctx.$t('label.type'),
                      "auto-grow": "",
                      rows: "1",
                      flat: ""
                    }, null, 8, ["modelValue", "label", "placeholder"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(RadioGroup, {
              modelValue: form.value.category,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.value.category) = $event)),
              labels: groupLabels.value,
              name: "category",
              label: _ctx.$t('label.whatGroup'),
              rules: "required",
              required: "",
              class: "mt-0",
              "label-font-weight": "medium"
            }, null, 8, ["modelValue", "labels", "label"]),
            _createVNode(CRZ, {
              modelValue: form.value.consents,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.value.consents) = $event)),
              fields: consentConfig.value.fieldsets
            }, null, 8, ["modelValue", "fields"]),
            (_ctx.captchaRequired)
              ? (_openBlock(), _createBlock(CaptchaField, {
                  key: 0,
                  modelValue: form.value.captchaToken,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((form.value.captchaToken) = $event)),
                  rules: "required",
                  name: "captcha",
                  required: ""
                }, null, 8, ["modelValue"]))
              : _createCommentVNode("", true),
            _createVNode(_component_v_btn, {
              id: "swr-step3-form-submit",
              color: "szRed",
              disabled: getLoader.value,
              loading: getLoader.value,
              class: "primary-button mt-5",
              variant: "flat",
              block: _ctx.$vuetify.display.xs,
              onClick: _unref(onSubmit)
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('family.confirmFamilyChoose')), 1)
              ]),
              _: 1
            }, 8, ["disabled", "loading", "block", "onClick"]),
            _createElementVNode("div", _hoisted_1, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(crzTextFields.value, (field, index) => {
                return (_openBlock(), _createBlock(ExpandableText, {
                  key: index,
                  text: field.options.label,
                  "additional-text": field.options.details
                }, null, 8, ["text", "additional-text"]))
              }), 128))
            ])
          ])
        ]),
        _: 1
      }),
      _createVNode(ModalCustom, {
        modelValue: isFamilyNotAvailable.value,
        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((isFamilyNotAvailable).value = $event))
      }, {
        header: _withCtx(() => [
          (donorResponse.value.isFamilyChosen)
            ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(_ctx.$t('family.chosenFamily')), 1))
            : (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_ctx.$t('family.otherLocation')), 1)),
          (donorResponse.value.isFamilyChosen)
            ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_ctx.$t('family.chooseOther')), 1))
            : _createCommentVNode("", true),
          _createVNode(_component_router_link, {
            to: { name: returnLink.value.toName, params: returnLink.value.params },
            class: "primary-button d-block d-md-inline-block pl-0 mt-5 mb-3 text-center"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('label.findOtherFamily')), 1)
            ]),
            _: 1
          }, 8, ["to"])
        ]),
        footer: _withCtx(() => [
          _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t('family.donateForSzp')), 1),
          _createElementVNode("a", {
            href: _unref(szpPaymentLink),
            target: "_blank",
            class: "secondary-button d-inline-block pl-0 mt-5 mb-3"
          }, _toDisplayString(_ctx.$t('label.donateForSzp')), 9, _hoisted_6)
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})